import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ToastType, UserProfile } from '../types';
import {
  LEGAL_SCHEMA_VERSION,
  TERMS_OF_SERVICE_PARAGRAPHS,
  TERMS_OF_SERVICE_TITLE,
  TOS_VERSION,
} from '../legal/legalText';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import RoddoSectionCard from './layout/RoddoSectionCard';

type Props = {
  userProfile: UserProfile;
  onBack: () => void;
  addToast: (msg: string, type?: ToastType) => void;
  onUpdateProfile: (nextProfile: UserProfile) => Promise<void> | void;
  isDarkMode?: boolean;
};

function toLegalTimestampMs(value: any): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (value && typeof value.toMillis === 'function') {
    try {
      const millis = Number(value.toMillis());
      return Number.isFinite(millis) && millis > 0 ? millis : null;
    } catch {}
  }

  if (value && typeof value.seconds === 'number') {
    const millis =
      Number(value.seconds) * 1000 +
      Math.floor(Number(value.nanoseconds || 0) / 1000000);
    return Number.isFinite(millis) && millis > 0 ? millis : null;
  }

  if (value instanceof Date) {
    const millis = value.getTime();
    return Number.isFinite(millis) && millis > 0 ? millis : null;
  }

  if (typeof value === 'string' && value.trim()) {
    const millis = Date.parse(value);
    return Number.isFinite(millis) && millis > 0 ? millis : null;
  }

  return null;
}

function formatDate(ts?: any) {
  const millis = toLegalTimestampMs(ts);
  if (!millis) return null;
  try {
    return new Date(millis).toLocaleString();
  } catch {
    return null;
  }
}

export default function TermsOfServiceView({ userProfile, onBack, addToast, onUpdateProfile, isDarkMode }: Props) {
  const { t } = useTranslation();
  const dark = typeof isDarkMode === 'boolean' ? isDarkMode : resolveRoddoDarkMode();
  const ui = getRoddoUiTokens(dark);
  const legal = ((userProfile as any)?.legal ?? {}) as any;
  const alreadyAcceptedVersion = legal?.tos?.version === TOS_VERSION;

  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const acceptedAtText = useMemo(() => formatDate(legal?.tos?.acceptedAt), [legal?.tos?.acceptedAt]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 10) setHasScrolledToEnd(true);
  };

  const accept = async () => {
    if (!hasScrolledToEnd || isAccepting) return;
    setIsAccepting(true);
    try {
      const now = Date.now();
      const nextProfile = {
        ...(userProfile as any),
        legal: {
          ...legal,
          schemaVersion: LEGAL_SCHEMA_VERSION,
          tos: { version: TOS_VERSION, acceptedAt: now },
        },
      } as UserProfile;
      await onUpdateProfile(nextProfile);
      addToast('Terms accepted.', 'success');
      onBack();
    } catch (error) {
      console.error(error);
      addToast('Could not save Terms acceptance.', 'error');
    } finally {
      setIsAccepting(false);
    }
  };

  const canAccept = hasScrolledToEnd && !isAccepting;

  return (
    <div className="flex min-h-0 flex-1 flex-col pb-3" data-roddo-theme={dark ? 'dark' : 'light'}>
      <RoddoSectionCard
        isDarkMode={dark}
        className="flex min-h-0 flex-1 flex-col overflow-hidden !p-0"
        style={{
          maxHeight:
            'calc(var(--roddo-visual-vh, 100dvh) - var(--roddo-safe-top, 0px) - var(--roddo-nav-h, 84px) - 28px)',
        }}
      >
        <div
          className="relative px-5 pb-5 pt-5 sm:px-6 sm:pt-6"
          style={{
            background: dark ? '#121A2B' : '#FFFCF7',
            borderBottom: `1px solid ${ui.border}`,
          }}
        >
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-lg font-black transition duration-150 ease-out hover:-translate-y-0.5 hover:brightness-110 active:translate-y-[1px]"
            style={{
              background: dark ? 'rgba(11,9,31,0.76)' : 'rgba(255,255,255,0.86)',
              border: `1px solid ${ui.borderStrong}`,
              color: ui.textPrimary,
              boxShadow: dark ? '0 4px 12px rgba(0,0,0,0.18)' : '0 4px 10px rgba(68,57,45,0.06)',
            }}
          >
            ‹
          </button>

          <div className="pr-12">
            <div
              className="mb-3 inline-flex rounded-full border px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em]"
              style={{ background: dark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.66)', borderColor: ui.border, color: ui.textMuted }}
            >
              {t('legal.legalDocument', 'Legal document')}
            </div>
            <h1 className="text-2xl font-black leading-tight" style={{ color: ui.textPrimary }}>
              {TERMS_OF_SERVICE_TITLE}
            </h1>
            <p className="mt-2 text-[12px] leading-5" style={{ color: ui.textSecondary }}>
              {t('common.version', 'Version')} {TOS_VERSION}{acceptedAtText ? <> · {t('common.acceptedAt', 'Accepted {{date}}', { date: acceptedAtText })}</> : null}
            </p>
          </div>
        </div>

        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-5 py-4 [-webkit-overflow-scrolling:touch] sm:px-6"
          style={{ color: ui.textPrimary, touchAction: 'pan-y' }}
        >
          {TERMS_OF_SERVICE_PARAGRAPHS.map((paragraph, index) => (
            <p key={index} className="whitespace-pre-line text-[13px] leading-6" style={{ color: ui.textSecondary }}>
              {paragraph}
            </p>
          ))}
          <div className="h-4" />
        </div>

        <div className="shrink-0 px-5 py-4 sm:px-6" style={{ background: ui.cardBgMuted, borderTop: `1px solid ${ui.borderStrong}` }}>
          <div className="text-[11px] font-bold" style={{ color: ui.textMuted }}>
            {hasScrolledToEnd ? t('legal.reachedEnd', 'You reached the end.') : t('legal.scrollToAccept', 'Scroll to the bottom to enable acceptance.')}
          </div>
          <button
            type="button"
            onClick={accept}
            disabled={!canAccept}
            className="mt-3 w-full rounded-[14px] px-4 py-3 text-sm font-black transition duration-150 ease-out hover:-translate-y-0.5 hover:brightness-105 active:translate-y-[1px] disabled:pointer-events-none disabled:opacity-45"
            style={{
              background: canAccept ? '#FFC94A' : dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
              color: canAccept ? '#2d2b42' : ui.textMuted,
              boxShadow: canAccept ? (dark ? '0 10px 22px rgba(246,196,83,0.16)' : '0 8px 18px rgba(130,98,17,0.14)') : 'none',
            }}
          >
            {isAccepting ? t('common.saving', 'Saving…') : alreadyAcceptedVersion ? t('legal.reacceptTerms', 'Re-accept Terms') : t('legal.acceptTerms', 'Accept Terms')}
          </button>
        </div>
      </RoddoSectionCard>
    </div>
  );
}
